<template>
  <div
      ref="snack"
      v-draggable="{ closable }"
      class="snack"
      :class="{
      'snack--draggable': closable,
      [`snack--${type.toLowerCase()}`]: type,
    }"
      @mouseover="onHover(true)"
      @mouseleave="onHover(false)"
      @draggable-close="close"
  >
    <div class="snack__inner">
      <!-- Content -->
      <div class="snack__content">
        <div class="snack__title">
          {{ title }}
        </div>
        <div
            v-if="message"
            class="snack__message"
        >
          {{ message }}
        </div>
      </div>

      <!-- Actions -->
      <div
          v-if="actions"
          class="snack__actions"
      >
        <div
            v-for="(action, i) in actions"
            :key="i"
            @click="action.action"
        >
          {{ action.label }}
        </div>
      </div>

      <!-- Timer -->
      <div
          ref="timer"
          class="snack__timer"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { TYPES } from '@/types/Snackbar'
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue"
import { useSnackbarStore } from "@/stores/useSnackbarStore"
import vDraggable from '@/plugins/directives/draggable'

const DEFAULT_LIFETIME = 3 // seconds

const props = defineProps({
  uuid: { type: String, required: true },
  title: { type: String, default: null },
  message: { type: String, default: null },
  icon: { type: String, default: null },
  actions: { type: Array, default: null },
  closable: { type: Boolean, default: true },
  lifetime: { type: Number, default: DEFAULT_LIFETIME }, // in seconds
  type: { type: String, default: TYPES.MESSAGE }, // MESSAGE | SUCCESS | WARNING | ERROR
  index: { type: Number, default: 0 },
})

const ttl = ref(props.lifetime * 1000)
const snack = useTemplateRef('snack')
const timer = useTemplateRef('timer')
let timeout = null

onMounted(() => {
  if (props.lifetime && props.lifetime > 0) {
    nextTick(start)
  }
})

onBeforeUnmount(() => {
  if (timeout) {
    clearTimeout(timeout)
  }
})

const { removeSnack } = useSnackbarStore()

function start() {
  timeout = setTimeout(close, ttl.value)

  // Start declining the width of the timer
  if (timer.value) {
    timer.value.style.transition = `width ${ttl.value}ms linear`
    timer.value.style.width = 0
  }
}

function pause() {
  ttl.value = props.lifetime * 1000
  clearTimeout(timeout)
  timeout = null

  // Set the width to match the time left
  if (timer.value) {
    timer.value.style.transition = 'width 0.5s ease-in'
    timer.value.style.width = `100%`
  }
}

function close() {
  if (props.uuid) {
    removeSnack(props.uuid)
  }
}

function onHover(over) {
  // don't start / stop timers if the toast has no lifetime
  if (!props.lifetime) return

  if (over && timeout) {
    pause()
  }

  if (!over && !timeout) {
    start()
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.snack {
  $self:    &;
  position: relative;
  opacity:  1;
  margin:   1rem;
  user-select: none;

  @include lg-up {
    right: 0;
    margin: 0;
    width: auto;
  }

  // Remove pointer events on children (important for hover functions)
  & * {
    pointer-events: none;
  }

  &__inner {
    @include heavy-shadow-md;
    @include radius;

    position: relative;
    background-color: #ffffff;
    display:  flex;
    padding:  1rem 3rem 1rem 1.5rem;
    width:    100%;
    overflow: hidden;
  }

  &__title {
    font-weight: 700;
  }

  &__actions {
    display: flex;
    gap: 1rem;
  }

  &__timer {
    width:      100%;
    height:     4px;
    background: var(--color-neutral-quaternary);
    position:   absolute;
    bottom:     0;
    right:      0;
    transform-origin: right center;
  }

  // Type Modifiers
  &--success {
    #{$self}__inner {
      color:           rgb(48, 86, 48);
      background:      rgb(155, 255, 165);
      background:      radial-gradient(
                               circle,
                               rgba(155, 255, 165, 1) 0%,
                               rgba(162, 255, 193, 1) 100%
                       );
      background-size: 200%;
      background-position: 0% 0%;
      border:          1px solid rgb(133, 227, 143);
    }

    #{$self}__timer {
      background-color: rgb(133, 227, 143);
    }
  }

  &--warning {
    #{$self}__inner {
      color:           rgb(86, 66, 48);
      background:      rgb(255, 246, 155);
      background:      radial-gradient(
                               circle,
                               rgba(255, 246, 155, 1) 0%,
                               rgba(255, 210, 162, 1) 100%
                       );
      background-size: 200%;
      background-position: 0% 0%;
      border:          1px solid rgb(240, 174, 133);
    }

    #{$self}__timer {
      background-color: rgb(240, 174, 133);
    }
  }

  &--error {
    #{$self}__inner {
      color:           rgb(86, 48, 48);
      background:      rgb(255, 170, 155);
      background:      radial-gradient(
                               circle,
                               rgba(255, 170, 155, 1) 0%,
                               rgba(255, 199, 162, 1) 100%
                       );
      background-size: 200%;
      background-position: 0% 0%;
      border:          1px solid rgb(240, 149, 133);
    }

    #{$self}__timer {
      background-color: rgb(240, 149, 133);
    }
  }

  &--draggable {
    cursor: grab;
  }
}
</style>
